import { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { GenericButton, NumberInput, SquareButton } from 'components';
import { useFormikContext } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useBoolModalState } from '@app/core';

const useStyles = createUseStyles((theme: Theme) => ({
  durationButton: {
    width: '100%',
    display: 'grid',
    alignItems: 'center',
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    border: `1px solid ${theme.colors.grayBorder}`,
    margin: 0,
  },
  foldableContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.colors.grayBackground,
    gap: theme.marginBase,
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    transition: 'width .3s ease',
  },
  label: { ...theme.fonts.h5 },
  iconContainer: {
    marginLeft: theme.marginBase * 2,
  },
  unitLabel: {
    ...theme.fonts.label,
    marginLeft: theme.marginBase,
    color: theme.colors.inkSecondary,
    whiteSpace: 'nowrap',
  },
  notEditedLabel: {
    ...theme.fonts.label,
    marginLeft: theme.marginBase,
    color: theme.colors.inkPrimary,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  selectedBorder: {
    border: `1px solid ${theme.colors.green}`,
  },
}));

interface Props {
  name: string;
  unit: 'min';
  step: number;
  min: number;
  initialValue?: number;
  isFolded: boolean;
  setIsFolded: (value: boolean) => void;
  trackingName?: string;
}

export const FoldableNumberInput = ({
  name,
  step,
  min,
  initialValue,
  isFolded,
  setIsFolded,
  trackingName,
}: Props) => {
  const classes = useStyles();

  const [isInEditionMode, startEditing, stopEditing] = useBoolModalState(false);
  const [editionValue, setEditionValue] = useState(initialValue);

  const formik = useFormikContext<any>();
  const setFieldValue = formik.setFieldValue;

  const { t } = useTranslation();

  const currentVal = formik.values[name];
  const prevVal = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (currentVal && currentVal !== prevVal.current) {
      setEditionValue(currentVal);
    }
    prevVal.current = currentVal;
  }, [editionValue, currentVal, setEditionValue, prevVal]);

  const onClick = useCallback(() => setIsFolded(false), [setIsFolded]);
  const onSubmit = useCallback(() => {
    if (!formik) return;
    setFieldValue(name, editionValue);
    stopEditing();
  }, [setFieldValue, name, editionValue, stopEditing]);

  useEffect(() => {
    if (isFolded) {
      stopEditing();
    }
    startEditing();
  }, [isFolded, stopEditing]);

  if (isFolded) {
    return (
      <GenericButton
        className={classnames('button', classes.durationButton)}
        onClick={onClick}
        title="select custom duration"
        type="button"
      >
        <div className={classes.label} aria-label="Label">
          <Trans i18nKey="studySetup.generalSetup.duration.customDuration" />
        </div>
      </GenericButton>
    );
  }

  if (!isInEditionMode) {
    return (
      <div className={classes.foldableContainer}>
        <div className={classes.notEditedLabel}>
          {t('studySetup.generalSetup.duration.minutes', {
            value: editionValue,
          })}
        </div>
        <SquareButton
          icon="ri-edit-line"
          color="green"
          title="Delete"
          onClick={startEditing}
          className={classes.iconContainer}
        />
      </div>
    );
  }

  return (
    <div
      className={classnames(classes.foldableContainer, classes.selectedBorder)}
    >
      <NumberInput
        step={step}
        min={min}
        noMargin
        small
        value={editionValue}
        onValueChange={(val: string) => {
          setEditionValue(Number(val));
        }}
        onSubmit={onSubmit}
        trackingName={trackingName}
      />
      <div className={classes.unitLabel}>
        {t('studySetup.generalSetup.duration.minutes', { value: editionValue })}
      </div>
      <SquareButton
        icon="ri-check-line"
        color="green"
        title="Delete"
        type="button"
        className={classes.iconContainer}
        onClick={onSubmit}
      />
    </div>
  );
};
